/**
 * MaintenanceGate — full-screen overlay shown when the backend returns a 503
 * with `detail === 'maintenance'`. Listens for the `cm:maintenance` custom
 * event dispatched by the Axios response interceptor in api/client.ts.
 *
 * Renders a simple, design-token–styled screen with a retry button (page
 * reload). Admin users keep access because the interceptor only fires for
 * 503 responses — the backend does not return 503 to admin-scoped routes.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function MaintenanceGate() {
  const { t } = useTranslation();
  const [active, setActive] = useState(false);

  useEffect(() => {
    const handler = () => setActive(true);
    window.addEventListener('cm:maintenance', handler);
    return () => window.removeEventListener('cm:maintenance', handler);
  }, []);

  if (!active) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="maint-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1.5rem',
        background: 'var(--bg, #0f172a)',
        color: 'var(--text, #f1f5f9)',
        textAlign: 'center',
        padding: '2rem',
      }}
    >
      {/* Icon */}
      <svg
        aria-hidden="true"
        width="56"
        height="56"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ opacity: 0.6 }}
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxWidth: 480 }}>
        <h1
          id="maint-title"
          style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}
        >
          {t('adm.settings.maint.gateTitle', { defaultValue: 'Maintenance in progress' })}
        </h1>
        <p style={{ margin: 0, opacity: 0.7, lineHeight: 1.6 }}>
          {t('adm.settings.maint.gateSub', {
            defaultValue:
              'The platform is temporarily unavailable for scheduled maintenance. We will be back shortly.',
          })}
        </p>
      </div>

      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{
          padding: '0.625rem 1.5rem',
          borderRadius: '0.5rem',
          border: '1px solid var(--line, rgba(255,255,255,0.15))',
          background: 'transparent',
          color: 'inherit',
          cursor: 'pointer',
          fontSize: '0.875rem',
          fontWeight: 600,
          transition: 'background 0.15s',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.08)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
        }}
      >
        {t('adm.settings.maint.gateRetry', { defaultValue: 'Retry' })}
      </button>
    </div>
  );
}
